/**
 * @file TaskShortageDialog.tsx
 * @description Исход «недостача» по заданию целиком (phase19.14, W13).
 *
 * Отличается от `ShortageDialog`: там отмечается нехватка по одной позиции,
 * здесь кладовщик закрывает задание с недостачей — заказ остаётся на сборке,
 * а решение (замена / частичная отгрузка / отмена) принимает менеджер.
 *
 * Сервер откажет (422 `picking_shortage_empty`), если ни по одной строке
 * недостача не отмечена: менеджеру нечего было бы разбирать.
 */

import { useState } from "react";

import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Label } from "../../ui/label";

interface Props {
  taskNumber: string;
  /** Список позиций с недостачей — что увидит менеджер. */
  lines: string[];
  saving: boolean;
  error: string | null;
  onConfirm: (note: string) => void;
  onClose: () => void;
}

export function TaskShortageDialog({
  taskNumber,
  lines,
  saving,
  error,
  onConfirm,
  onClose,
}: Props) {
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  function submit() {
    if (!note.trim()) {
      setLocalError("Опишите ситуацию — по этому тексту менеджер примет решение.");
      return;
    }
    setLocalError(null);
    onConfirm(note.trim());
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Оформить недостачу по {taskNumber}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto text-sm">
          {lines.length > 0 ? (
            <div className="rounded-xl bg-surface-low p-3">
              <div className="mb-1 font-medium">Не хватило:</div>
              <ul className="flex list-disc flex-col gap-1 pl-5 text-on-surface-variant">
                {lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : (
            <Alert severity="warning">
              Сначала отметьте недостачу хотя бы по одной позиции — иначе
              менеджеру нечего разбирать.
            </Alert>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-shortage-note">Комментарий для менеджера *</Label>
            <textarea
              id="task-shortage-note"
              rows={4}
              maxLength={500}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setLocalError(null);
              }}
              disabled={saving}
              placeholder="Например: «Сиропа облепихи на месте нет, на складе тоже не нашли»"
              className="rounded-xl border border-on-surface/15 bg-surface-lowest p-3 text-base"
            />
          </div>

          <p className="text-xs text-on-surface-variant">
            Заказ останется на стадии «На сборке» до решения менеджера. Задание
            попадёт в «Требуют внимания».
          </p>

          {localError ?? error ? (
            <Alert severity="error">{localError ?? error}</Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="min-h-12" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            className="min-h-12"
            onClick={submit}
            disabled={saving || lines.length === 0}
          >
            {saving ? "Сохраняем…" : "Оформить недостачу"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
